import { useState, useEffect, useRef } from 'react';
import { readOrgFile, writeOrgFile } from '../../storage/FileSystemAdapter';
import { appendChangeLog } from '../../utils/changeLog';
import { callWorker } from '../../utils/workerClient';
import { BG_SUBTLE, BTN_PRIMARY, BTN_SECONDARY, BTN_DESTRUCTIVE_OUTLINE, TEXT_MUTED, TEXT_DISABLED, BORDER_DEFAULT, TEXT_HEADING, TEXT_DEFAULT, BG_SURFACE, BRAND_PRIMARY, BRAND_PRIMARY_DARK, ERROR_TEXT, ERROR_BG, ERROR_BORDER, BG_MUTED, STATUS_GOOD, BG_SHELL, STATUS_WARN, UNSAVED_AMBER, SURFACE_WHITE, FONT_PAGE_TITLE, FONT_BODY, FONT_BODY_STRONG, FONT_LABEL, FONT_SMALL, FONT_TINY, FONT_BUTTON, FONT_STAT_VALUE } from '../../theme';
import PeopleSelector, { resolvePerson } from '../shared/PeopleSelector';
import TagEditor from '../shared/TagEditor';
import AISuggestButton from '../shared/AISuggestButton';
import ConfirmDialog from '../shared/ConfirmDialog';

const TRAINING_TYPES    = ['Training', 'Mentoring', 'Experience', 'External cert'];
const TRAINING_STATUSES = ['Planned', 'In Progress', 'Completed'];

const TRAINING_STATUS_COLORS = {
  'Planned':     { bg: BG_MUTED, text: TEXT_MUTED },
  'In Progress': { bg: STATUS_WARN.bg, text: STATUS_WARN.text },
  'Completed':   { bg: STATUS_GOOD.bg, text: BRAND_PRIMARY_DARK },
};

const AI_LABEL_COMPETENCES = { es: 'Sugerir competencias ✨', en: 'Suggest competences ✨' };
const AI_LABEL_ACTIONS     = { es: 'Sugerir acciones ✨',     en: 'Suggest actions ✨' };


function genId(prefix) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

// ── I18N ──────────────────────────────────────────────────────────────────────
const T = {
  es: {
    title:                'Programa de competencias',
    prevYear:             '←',
    nextYear:             '→',
    save:                 'Guardar',
    saving:               'Guardando…',
    saveOk:               '✓ Guardado',
    saveError:            'Error al guardar',
    unsavedLabel:         'Cambios sin guardar',
    unsavedConfirm:       'Hay cambios sin guardar. ¿Salir de todas formas?',
    leaveWithoutSaving:   'Salir sin guardar',
    saveAndLeave:         'Guardar y salir',
    formCancel:           'Cancelar',
    loading:              'Cargando…',
    cntRoles:             'Roles',
    cntActions:           'Acciones formativas',
    cntCompleted:         'Completadas',
    addRole:              '+ Añadir rol',
    deleteRoleBtn:        'Eliminar',
    deleteRoleConfirm:    '¿Eliminar el rol "{name}"? Esta acción no se puede deshacer.',
    deleteActionConfirm:  '¿Eliminar esta acción formativa?',
    deleteBtn:            'Eliminar',
    unnamedRole:          'rol sin nombre',
    fRole:                'Nombre del rol',
    fPersonIds:           'Personas en este rol',
    fJobDescription:      'Descripción del puesto',
    jobDescriptionHint:   'Opcional. Da contexto a las sugerencias de IA.',
    fCompetences:         'Competencias requeridas',
    fAssessmentNotes:     'Evaluación de competencias actual',
    tagPlaceholder:       'Añadir y pulsar Enter…',
    trainingActionsTitle: 'Acciones formativas',
    addAction:            '+ Añadir acción',
    colTitle:             'Título',
    colType:              'Tipo',
    colResponsible:       'Responsable',
    colDeadline:          'Fecha límite',
    colStatus:            'Estado',
    colEvidenceId:        'ID evidencia',
    colDelete:            '',
    colActions:           'Acciones',
    editActionBtn:        'Editar',
    saveActionBtn:        'Guardar',
    cancelActionBtn:      'Cancelar',
    newActionTitle:       'Nueva acción formativa',
    editActionTitle:      'Editar acción formativa',
    titlePlaceholder:     'p. ej. Curso ISO 27001 LA',
    evidencePlaceholder:  'p. ej. EV-2026-001',
    actionsProgress:      '{done}/{total} completadas',
    peopleCount:          '{n} persona(s)',
    competencesCount:     '{n} competencia(s)',
    emptyRoles:           'No hay roles definidos.',
    emptyRolesHint:       'Añade el primer rol con el botón "+ Añadir rol".',
    aiError:              'Error al conectar con la IA. Inténtalo de nuevo.',
    aiErrorOverloaded:    'El servidor de IA está ocupado. Inténtalo más tarde.',
    aiNoConsent:          'Consentimiento de procesamiento IA no concedido (ver Configuración).',
    typeLabels: {
      'Training':      'Formación',
      'Mentoring':     'Mentoría',
      'Experience':    'Experiencia',
      'External cert': 'Certificación externa',
    },
    statusLabels: {
      'Planned':     'Planificado',
      'In Progress': 'En curso',
      'Completed':   'Completado',
    },
  },
  en: {
    title:                'Competence program',
    prevYear:             '←',
    nextYear:             '→',
    save:                 'Save',
    saving:               'Saving…',
    saveOk:               '✓ Saved',
    saveError:            'Save error',
    unsavedLabel:         'Unsaved changes',
    unsavedConfirm:       'You have unsaved changes. Leave anyway?',
    leaveWithoutSaving:   'Leave without saving',
    saveAndLeave:         'Save and leave',
    formCancel:           'Cancel',
    loading:              'Loading…',
    cntRoles:             'Roles',
    cntActions:           'Training actions',
    cntCompleted:         'Completed',
    addRole:              '+ Add role',
    deleteRoleBtn:        'Delete',
    deleteRoleConfirm:    'Delete role "{name}"? This cannot be undone.',
    deleteActionConfirm:  'Delete this training action?',
    deleteBtn:            'Delete',
    unnamedRole:          'unnamed role',
    fRole:                'Role name',
    fPersonIds:           'People in this role',
    fJobDescription:      'Job description',
    jobDescriptionHint:   'Optional. Provides context for AI suggestions.',
    fCompetences:         'Required competences',
    fAssessmentNotes:     'Current competence assessment',
    tagPlaceholder:       'Add and press Enter…',
    trainingActionsTitle: 'Training actions',
    addAction:            '+ Add action',
    colTitle:             'Title',
    colType:              'Type',
    colResponsible:       'Responsible',
    colDeadline:          'Deadline',
    colStatus:            'Status',
    colEvidenceId:        'Evidence ID',
    colDelete:            '',
    colActions:           'Actions',
    editActionBtn:        'Edit',
    saveActionBtn:        'Save',
    cancelActionBtn:      'Cancel',
    newActionTitle:       'New training action',
    editActionTitle:      'Edit training action',
    titlePlaceholder:     'e.g. ISO 27001 Lead Implementer course',
    evidencePlaceholder:  'e.g. EV-2026-001',
    actionsProgress:      '{done}/{total} completed',
    peopleCount:          '{n} person(s)',
    competencesCount:     '{n} competence(s)',
    emptyRoles:           'No roles defined.',
    emptyRolesHint:       'Add the first role using the "+ Add role" button.',
    aiError:              'Error connecting to AI. Please try again.',
    aiErrorOverloaded:    'AI server is busy. Please try again later.',
    aiNoConsent:          'AI processing consent not granted (see Settings).',
    typeLabels: {
      'Training':      'Training',
      'Mentoring':     'Mentoring',
      'Experience':    'Experience',
      'External cert': 'External cert',
    },
    statusLabels: {
      'Planned':     'Planned',
      'In Progress': 'In Progress',
      'Completed':   'Completed',
    },
  },
};

// ── SUMMARY CARD ──────────────────────────────────────────────────────────────
function SummaryCard({ value, label, color }) {
  return (
    <div style={{
      background: BG_SURFACE, border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 10,
      padding: '0.9rem 1.25rem',
    }}>
      <div style={{
        ...FONT_SMALL,
        color: TEXT_DEFAULT, letterSpacing: '0.04em',
        textTransform: 'uppercase', marginBottom: '0.35rem',
      }}>
        {label}
      </div>
      <div style={{
        ...FONT_STAT_VALUE, color,
      }}>
        {value}
      </div>
    </div>
  );
}

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
export default function CompetenceView({ org, lang, onBack, onDirtyChange, saveRequestTick, onSaveResult }) {
  const t        = T[lang] || T.en;
  const noConsent = org.aiProcessingConsent === false;

  const [program,        setProgram]        = useState(null);
  const [year,           setYear]           = useState(new Date().getFullYear());
  const [saveState,      setSaveState]      = useState('idle');
  const [dirty,          setDirty]          = useState(false);
  const [confirmState,   setConfirmState]   = useState(null);
  const [expandedRoles,  setExpandedRoles]  = useState(new Set());
  const [aiCompetencesState, setAiCompetencesState] = useState({});
  const [aiActionsState,     setAiActionsState]     = useState({});
  const [people,             setPeople]             = useState([]);
  const [editingAction,      setEditingAction]      = useState(null);
  const [actionForm,         setActionForm]         = useState({ title: '', type: 'Training', responsible: '', deadline: '', status: 'Planned', evidenceId: '' });

  const savedProgramRef = useRef(null);

  // ── Load program for year ─────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setProgram(null);
    setDirty(false);
    (async () => {
      try {
        const data = await readOrgFile(org.id, `competence/program-${year}.json`);
        if (!cancelled) {
          setProgram(data);
          savedProgramRef.current = data;
        }
      } catch {
        const blank = {
          id: `competence-${org.id}-${year}`,
          orgId: org.id,
          year,
          roles: [],
          updatedAt: new Date().toISOString(),
          changeLog: [],
        };
        if (!cancelled) {
          setProgram(blank);
          savedProgramRef.current = blank;
        }
      }
    })();
    return () => { cancelled = true; };
  }, [org.id, year]);

  // ── Load people for resolvePerson ─────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    readOrgFile(org.id, 'people.json')
      .then(data => { if (!cancelled) setPeople(Array.isArray(data.people) ? data.people : []); })
      .catch(() => { if (!cancelled) setPeople([]); });
    return () => { cancelled = true; };
  }, [org.id]);

  // ── beforeunload guard ────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  // ── Propagate dirty state to App.js navigation guard ─────────────────────
  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);

  // ── Save ──────────────────────────────────────────────────────────────────
  const doSave = async () => {
    if (!program || saveState === 'saving') return false;
    setSaveState('saving');
    try {
      const changedBy = org.consultantPersonId || 'system';
      const updated   = { ...program, updatedAt: new Date().toISOString() };
      updated.changeLog = appendChangeLog(savedProgramRef.current, updated, changedBy);
      await writeOrgFile(org.id, `competence/program-${year}.json`, updated);
      savedProgramRef.current = { ...updated };
      setProgram(updated);
      setDirty(false);
      setSaveState('ok');
      setTimeout(() => setSaveState('idle'), 2000);
      return true;
    } catch {
      setSaveState('error');
      setTimeout(() => setSaveState('idle'), 3000);
      return false;
    }
  };

  const handleSave = () => { doSave(); };

  // ── App-level guard: save on tick increase, report result back ───────────
  const lastProcessedTickRef = useRef(0);
  useEffect(() => {
    if (typeof saveRequestTick !== 'number') return;
    if (saveRequestTick <= lastProcessedTickRef.current) return;
    lastProcessedTickRef.current = saveRequestTick;
    (async () => {
      const ok = await doSave();
      onSaveResult?.(ok);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveRequestTick]);

  // ── Year navigation with dirty guard ─────────────────────────────────────
  const handleYearNav = (delta) => {
    if (dirty) {
      setConfirmState({
        message:      t.unsavedConfirm,
        confirmLabel: t.leaveWithoutSaving,
        onConfirm:    () => { setConfirmState(null); setYear(y => y + delta); },
        secondaryAction: {
          label:   t.saveAndLeave,
          onClick: async () => {
            setConfirmState(null);
            const ok = await doSave();
            if (ok) setYear(y => y + delta);
          },
        },
      });
      return;
    }
    setYear(y => y + delta);
  };

  // ── Role operations ───────────────────────────────────────────────────────
  const handleAddRole = () => {
    const newRole = {
      id:                        genId('role'),
      role:                      '',
      jobDescription:            '',
      personIds:                 [],
      requiredCompetences:       [],
      competenceAssessmentNotes: '',
      trainingActions:           [],
    };
    setDirty(true);
    setProgram(prev => ({ ...prev, roles: [...prev.roles, newRole] }));
    setExpandedRoles(prev => new Set([...prev, newRole.id]));
  };

  const setRoleField = (roleId, field, value) => {
    setDirty(true);
    setProgram(prev => ({
      ...prev,
      roles: prev.roles.map(r => r.id === roleId ? { ...r, [field]: value } : r),
    }));
  };

  const handleDeleteRole = (roleId, roleName) => {
    setConfirmState({
      message:      t.deleteRoleConfirm.replace('{name}', roleName || t.unnamedRole),
      confirmLabel: t.deleteBtn,
      onConfirm: () => {
        setConfirmState(null);
        setDirty(true);
        setProgram(prev => ({ ...prev, roles: prev.roles.filter(r => r.id !== roleId) }));
        setExpandedRoles(prev => { const next = new Set(prev); next.delete(roleId); return next; });
      },
    });
  };

  const handleToggleRole = (roleId) => {
    setExpandedRoles(prev => {
      const next = new Set(prev);
      if (next.has(roleId)) next.delete(roleId);
      else next.add(roleId);
      return next;
    });
  };

  // ── Training action operations ────────────────────────────────────────────
  const handleOpenNewActionForm = (roleId) => {
    setEditingAction({ roleId, actionId: 'new' });
    setActionForm({ title: '', type: 'Training', responsible: '', deadline: '', status: 'Planned', evidenceId: '' });
  };

  const handleOpenEditActionForm = (roleId, action) => {
    setEditingAction({ roleId, actionId: action.id });
    setActionForm({
      title:       action.title,
      type:        action.type,
      responsible: action.responsible ?? '',
      deadline:    action.deadline ?? '',
      status:      action.status,
      evidenceId:  action.evidenceId ?? '',
    });
  };

  const handleCancelActionForm = () => {
    setEditingAction(null);
  };

  const handleSaveActionForm = () => {
    if (!actionForm.title.trim()) return;
    const { roleId, actionId } = editingAction;
    if (actionId === 'new') {
      const newAction = {
        id:          genId('ta'),
        title:       actionForm.title,
        type:        actionForm.type,
        responsible: actionForm.responsible,
        deadline:    actionForm.deadline,
        status:      actionForm.status,
        evidenceId:  actionForm.evidenceId || null,
      };
      setProgram(prev => ({
        ...prev,
        roles: prev.roles.map(r =>
          r.id === roleId ? { ...r, trainingActions: [...r.trainingActions, newAction] } : r
        ),
      }));
    } else {
      setProgram(prev => ({
        ...prev,
        roles: prev.roles.map(r => {
          if (r.id !== roleId) return r;
          return {
            ...r,
            trainingActions: r.trainingActions.map(a =>
              a.id === actionId
                ? { ...a, title: actionForm.title, type: actionForm.type, responsible: actionForm.responsible, deadline: actionForm.deadline, status: actionForm.status, evidenceId: actionForm.evidenceId || null }
                : a
            ),
          };
        }),
      }));
    }
    setDirty(true);
    setEditingAction(null);
  };

  const handleDeleteAction = (roleId, actionId) => {
    setConfirmState({
      message:      t.deleteActionConfirm,
      confirmLabel: t.deleteBtn,
      onConfirm: () => {
        setConfirmState(null);
        setDirty(true);
        setProgram(prev => ({
          ...prev,
          roles: prev.roles.map(r =>
            r.id === roleId
              ? { ...r, trainingActions: r.trainingActions.filter(a => a.id !== actionId) }
              : r
          ),
        }));
      },
    });
  };

  // ── AI: suggest required competences ─────────────────────────────────────
  const handleSuggestCompetences = async (roleId) => {
    const role = (program.roles || []).find(r => r.id === roleId);
    if (!role) return;
    setAiCompetencesState(prev => ({ ...prev, [roleId]: { loading: true, error: null } }));
    const prompt = [
      'You are an ISO 27001:2022 Lead Implementer assistant.',
      'Suggest 4-6 specific information security competences expected for the given role in this organization.',
      'Base the competences on the role responsibilities. The technologies below are the organization-wide inventory and are not all relevant to this role: consider ONLY the technologies that the role responsibilities (per the job description) actually involve, and do not force a competence for every listed technology.',
      `Sector: ${org.sector || 'Not specified'}`,
      `Organization technology inventory: ${(org.technologies || []).join(', ') || 'Not specified'}`,
      `Organization size: ${org.size || 'Not specified'}`,
      `Role: ${role.role || 'Not specified'}`,
      `Job description: ${role.jobDescription || 'Not specified'}`,
      'Respond ONLY with a valid JSON array of strings. No markdown, no preamble.',
      'Example: ["Risk assessment","ISO 27001 internal auditing","Security incident handling"]',
    ].join('\n');
    const result = await callWorker({ prompt, maxTokens: 400 });
    if (!result.ok) {
      const busy = result.error === 'overloaded' || result.error === 'timeout';
      setAiCompetencesState(prev => ({
        ...prev,
        [roleId]: { loading: false, error: busy ? t.aiErrorOverloaded : t.aiError },
      }));
      return;
    }
    let parsed;
    try { parsed = JSON.parse(result.text); } catch {
      setAiCompetencesState(prev => ({
        ...prev,
        [roleId]: { loading: false, error: t.aiError },
      }));
      return;
    }
    if (Array.isArray(parsed) && parsed.length > 0) {
      const newItems = parsed.filter(s => typeof s === 'string' && s.trim());
      setProgram(prev => ({
        ...prev,
        roles: prev.roles.map(r => {
          if (r.id !== roleId) return r;
          const existing = new Set(r.requiredCompetences);
          const toAdd = newItems.filter(s => !existing.has(s));
          return { ...r, requiredCompetences: [...r.requiredCompetences, ...toAdd] };
        }),
      }));
      setDirty(true);
    }
    setAiCompetencesState(prev => ({ ...prev, [roleId]: { loading: false, error: null } }));
  };

  // ── AI: suggest training actions ──────────────────────────────────────────
  const handleSuggestActions = async (roleId) => {
    const role = (program.roles || []).find(r => r.id === roleId);
    if (!role) return;
    setAiActionsState(prev => ({ ...prev, [roleId]: { loading: true, error: null } }));
    const prompt = [
      'You are an ISO 27001:2022 Lead Implementer assistant.',
      'Based on the role, required competences, and current assessment, suggest 2-4 specific training actions to address competence gaps.',
      `Sector: ${org.sector || 'Not specified'}`,
      `Technologies: ${(org.technologies || []).join(', ') || 'Not specified'}`,
      `Organization size: ${org.size || 'Not specified'}`,
      `Role: ${role.role || 'Not specified'}`,
      `Job description: ${role.jobDescription || 'Not specified'}`,
      `Required competences: ${role.requiredCompetences.join(', ') || 'Not specified'}`,
      `Current competence assessment: ${role.competenceAssessmentNotes || 'Not yet assessed.'}`,
      'Respond ONLY with a valid JSON array, no markdown, no preamble.',
      'Each item must have exactly these fields: title (string), type (one of "Training", "Mentoring", "Experience", "External cert"), deadline (null), status ("Planned"), evidenceId (null).',
      'Example: [{"title":"ISO 27001 Lead Implementer Course","type":"Training","deadline":null,"status":"Planned","evidenceId":null}]',
    ].join('\n');
    const result = await callWorker({ prompt, maxTokens: 600 });
    if (!result.ok) {
      const busy = result.error === 'overloaded' || result.error === 'timeout';
      setAiActionsState(prev => ({
        ...prev,
        [roleId]: { loading: false, error: busy ? t.aiErrorOverloaded : t.aiError },
      }));
      return;
    }
    let parsed;
    try { parsed = JSON.parse(result.text); } catch {
      setAiActionsState(prev => ({
        ...prev,
        [roleId]: { loading: false, error: t.aiError },
      }));
      return;
    }
    if (Array.isArray(parsed) && parsed.length > 0) {
      const newActions = parsed.map(a => ({
        id:          genId('ta'),
        title:       typeof a.title === 'string' ? a.title : '',
        type:        TRAINING_TYPES.includes(a.type) ? a.type : 'Training',
        responsible: '',
        deadline:    typeof a.deadline === 'string' ? a.deadline : '',
        status:      'Planned',
        evidenceId:  null,
      }));
      setProgram(prev => ({
        ...prev,
        roles: prev.roles.map(r => {
          if (r.id !== roleId) return r;
          return { ...r, trainingActions: [...r.trainingActions, ...newActions] };
        }),
      }));
      setDirty(true);
    }
    setAiActionsState(prev => ({ ...prev, [roleId]: { loading: false, error: null } }));
  };

  // ── Loading state ─────────────────────────────────────────────────────────
  if (program === null) {
    return (
      <div style={{ ...FONT_LABEL, padding: '2rem', color: TEXT_MUTED }}>
        {t.loading}
      </div>
    );
  }

  // ── Computed ──────────────────────────────────────────────────────────────
  const roles           = program.roles || [];
  const totalRoles      = roles.length;
  const totalActions    = roles.reduce((sum, r) => sum + (r.trainingActions?.length || 0), 0);
  const completedActions = roles.reduce(
    (sum, r) => sum + (r.trainingActions?.filter(a => a.status === 'Completed').length || 0), 0
  );

  const saveLabel = { idle: t.save, saving: t.saving, ok: t.saveOk, error: t.save }[saveState];
  const saveBg    = { idle: BRAND_PRIMARY, saving: TEXT_MUTED, ok: BRAND_PRIMARY_DARK, error: BRAND_PRIMARY }[saveState];
  const isSaving  = saveState === 'saving';

  // ── Shared styles ─────────────────────────────────────────────────────────
  const inputSt = {
    ...FONT_BODY,
    width: '100%',
    border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 6, padding: '0.45rem 0.65rem',
    background: BG_SURFACE, color: TEXT_DEFAULT, outline: 'none', boxSizing: 'border-box',
  };

  const labelSt = {
    ...FONT_TINY, color: TEXT_DISABLED,
    letterSpacing: '0.04em', textTransform: 'uppercase', display: 'block', marginBottom: '0.35rem',
  };

  const thSt = {
    ...FONT_TINY, color: TEXT_MUTED,
    letterSpacing: '0.05em', textTransform: 'uppercase', padding: '0.4rem 0.6rem',
    textAlign: 'left', borderBottom: `1px solid ${BORDER_DEFAULT}`, whiteSpace: 'nowrap',
    background: BG_SUBTLE,
  };

  const tdSt = {
    ...FONT_LABEL,
    padding: '0.3rem 0.5rem', borderBottom: `1px solid ${BG_SHELL}`, verticalAlign: 'middle',
    color: TEXT_DEFAULT,
  };

  const cellInputSt = {
    ...FONT_LABEL,
    width: '100%',
    border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 4, padding: '0.25rem 0.4rem',
    background: BG_SURFACE, color: TEXT_DEFAULT, outline: 'none', boxSizing: 'border-box',
  };

  return (
    <div>
      <ConfirmDialog
        open={confirmState !== null}
        message={confirmState?.message}
        onConfirm={confirmState?.onConfirm}
        onCancel={() => setConfirmState(null)}
        confirmLabel={confirmState?.confirmLabel || t.deleteBtn}
        cancelLabel={t.formCancel}
        secondaryAction={confirmState?.secondaryAction}
      />

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem',
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <h1 style={{
              ...FONT_PAGE_TITLE,
              color: TEXT_HEADING, margin: 0,
            }}>
              {t.title}
            </h1>
            {/* Year navigation */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: '0.35rem',
              border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 8,
              padding: '0.2rem 0.5rem', background: BG_SURFACE,
            }}>
              <button
                onClick={() => handleYearNav(-1)}
                style={{
                  ...FONT_LABEL,
                  background: 'none', border: 'none', cursor: 'pointer', color: TEXT_MUTED,
                  padding: '0 0.1rem', lineHeight: 1,
                }}
              >
                {t.prevYear}
              </button>
              <span style={{
                ...FONT_LABEL, color: TEXT_HEADING, fontWeight: 600,
                minWidth: '3rem', textAlign: 'center', letterSpacing: '0.04em',
              }}>
                {year}
              </span>
              <button
                onClick={() => handleYearNav(1)}
                style={{
                  ...FONT_LABEL,
                  background: 'none', border: 'none', cursor: 'pointer', color: TEXT_MUTED,
                  padding: '0 0.1rem', lineHeight: 1,
                }}
              >
                {t.nextYear}
              </button>
            </div>
          </div>
        </div>

        {/* Right: save error */}
        {saveState === 'error' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', alignSelf: 'flex-end' }}>
            <span style={{
              ...FONT_LABEL,
              background: ERROR_BG, color: ERROR_TEXT, border: `1px solid ${ERROR_BORDER}`,
              borderRadius: 6, padding: '0.35rem 0.75rem',
            }}>
              {t.saveError}
            </span>
          </div>
        )}
      </div>

      {/* ── Summary strip ──────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem', marginBottom: '1.5rem' }}>
        <SummaryCard value={totalRoles}       label={t.cntRoles}     color={TEXT_HEADING} />
        <SummaryCard value={totalActions}     label={t.cntActions}   color={TEXT_HEADING} />
        <SummaryCard value={completedActions} label={t.cntCompleted} color={BRAND_PRIMARY_DARK} />
      </div>

      {/* ── Empty state ────────────────────────────────────────────────────── */}
      {roles.length === 0 && (
        <div style={{
          background: BG_SURFACE, border: `1px solid ${BORDER_DEFAULT}`,
          borderRadius: 10, padding: '3rem', textAlign: 'center',
        }}>
          <p style={{ ...FONT_BODY, color: TEXT_MUTED, margin: '0 0 0.5rem' }}>
            {t.emptyRoles}
          </p>
          <p style={{ ...FONT_LABEL, color: TEXT_DISABLED, margin: 0 }}>
            {t.emptyRolesHint}
          </p>
        </div>
      )}

      {/* ── Roles list ─────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {roles.map((role) => {
          const isExpanded     = expandedRoles.has(role.id);
          const doneActions    = (role.trainingActions || []).filter(a => a.status === 'Completed').length;
          const totalRoleActs  = (role.trainingActions || []).length;
          const aiCompetences  = aiCompetencesState[role.id] || { loading: false, error: null };
          const aiActions      = aiActionsState[role.id]    || { loading: false, error: null };

          return (
            <div key={role.id} style={{
              background: BG_SURFACE, border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 10, overflow: 'hidden',
            }}>
              {/* Card header */}
              <div
                onClick={() => handleToggleRole(role.id)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '0.85rem 1rem', cursor: 'pointer', userSelect: 'none',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1, minWidth: 0 }}>
                  <span style={{
                    ...FONT_LABEL,
                    color: TEXT_MUTED, flexShrink: 0,
                  }}>
                    {isExpanded ? '▼' : '▶'}
                  </span>
                  <span style={{
                    ...FONT_BODY_STRONG,
                    color: role.role ? TEXT_HEADING : TEXT_DISABLED,
                    fontStyle: role.role ? 'normal' : 'italic',
                    flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {role.role || t.unnamedRole}
                  </span>

                  {!isExpanded && (
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexShrink: 0 }}>
                      {role.personIds.length > 0 && (
                        <span style={{
                          ...FONT_TINY, color: TEXT_MUTED,
                          background: BG_SUBTLE, borderRadius: 4, padding: '0.1rem 0.4rem',
                        }}>
                          {t.peopleCount.replace('{n}', role.personIds.length)}
                        </span>
                      )}
                      {role.requiredCompetences.length > 0 && (
                        <span style={{
                          ...FONT_TINY, color: TEXT_MUTED,
                          background: BG_SUBTLE, borderRadius: 4, padding: '0.1rem 0.4rem',
                        }}>
                          {t.competencesCount.replace('{n}', role.requiredCompetences.length)}
                        </span>
                      )}
                      {totalRoleActs > 0 && (
                        <span style={{
                          ...FONT_TINY,
                          color: doneActions === totalRoleActs ? BRAND_PRIMARY_DARK : STATUS_WARN.text,
                          background: doneActions === totalRoleActs ? STATUS_GOOD.bg : STATUS_WARN.bg,
                          borderRadius: 4, padding: '0.1rem 0.4rem',
                        }}>
                          {t.actionsProgress.replace('{done}', doneActions).replace('{total}', totalRoleActs)}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                <button
                  onClick={(e) => { e.stopPropagation(); handleDeleteRole(role.id, role.role); }}
                  style={{
                    ...BTN_DESTRUCTIVE_OUTLINE, ...FONT_SMALL, borderRadius: 6, padding: '0.2rem 0.6rem',
                    cursor: 'pointer',
                    flexShrink: 0, marginLeft: '0.75rem',
                  }}
                >
                  {t.deleteRoleBtn}
                </button>
              </div>

              {/* Expanded content */}
              {isExpanded && (
                <div style={{ borderTop: `1px solid ${BG_SHELL}`, padding: '1rem 1rem 1.25rem' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

                    {/* Role name */}
                    <div>
                      <label style={labelSt}>{t.fRole} *</label>
                      <input
                        value={role.role}
                        onChange={e => setRoleField(role.id, 'role', e.target.value)}
                        style={inputSt}
                      />
                    </div>

                    {/* People */}
                    <div>
                      <label style={labelSt}>{t.fPersonIds}</label>
                      <PeopleSelector
                        orgId={org.id}
                        lang={lang}
                        value={role.personIds}
                        onChange={val => setRoleField(role.id, 'personIds', val)}
                        multiple
                      />
                    </div>

                    {/* Job description */}
                    <div>
                      <label style={labelSt}>{t.fJobDescription}</label>
                      <textarea
                        value={role.jobDescription || ''}
                        onChange={e => setRoleField(role.id, 'jobDescription', e.target.value)}
                        rows={3}
                        style={{ ...inputSt, resize: 'vertical', lineHeight: 1.5 }}
                      />
                      <span style={{ ...FONT_TINY, color: TEXT_MUTED, display: 'block', marginTop: '0.2rem' }}>
                        {t.jobDescriptionHint}
                      </span>
                    </div>

                    {/* Competences + AI suggest */}
                    <div>
                      <div style={{
                        display: 'flex', alignItems: 'center',
                        justifyContent: 'space-between', marginBottom: '0.35rem',
                      }}>
                        <label style={{ ...labelSt, marginBottom: 0 }}>{t.fCompetences}</label>
                        <AISuggestButton
                          onClick={() => handleSuggestCompetences(role.id)}
                          loading={aiCompetences.loading}
                          disabled={noConsent || aiCompetences.loading}
                          lang={lang}
                          label={AI_LABEL_COMPETENCES}
                        />
                      </div>
                      <TagEditor
                        tags={role.requiredCompetences}
                        onChange={tags => setRoleField(role.id, 'requiredCompetences', tags)}
                        placeholder={t.tagPlaceholder}
                      />
                      {noConsent && (
                        <span style={{
                          ...FONT_TINY,
                          color: TEXT_DISABLED, display: 'block', marginTop: '0.2rem',
                        }}>
                          {t.aiNoConsent}
                        </span>
                      )}
                      {aiCompetences.error && (
                        <span style={{
                          ...FONT_SMALL,
                          color: ERROR_TEXT, display: 'block', marginTop: '0.2rem',
                        }}>
                          {aiCompetences.error}
                        </span>
                      )}
                    </div>

                    {/* Assessment notes — no AI */}
                    <div>
                      <label style={labelSt}>{t.fAssessmentNotes}</label>
                      <textarea
                        value={role.competenceAssessmentNotes}
                        onChange={e => setRoleField(role.id, 'competenceAssessmentNotes', e.target.value)}
                        rows={3}
                        style={{ ...inputSt, resize: 'vertical', lineHeight: 1.5 }}
                      />
                    </div>

                    {/* Training actions */}
                    <div>
                      <div style={{
                        ...FONT_TINY, color: TEXT_MUTED,
                        letterSpacing: '0.05em', textTransform: 'uppercase',
                        borderTop: `1px solid ${BORDER_DEFAULT}`, paddingTop: '1rem', marginBottom: '0.75rem',
                      }}>
                        {t.trainingActionsTitle}
                      </div>

                      {role.trainingActions.length > 0 && (
                        <div style={{
                          overflowX: 'auto', marginBottom: '0.75rem',
                          borderRadius: 6, border: `1px solid ${BORDER_DEFAULT}`,
                        }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', background: SURFACE_WHITE }}>
                            <thead>
                              <tr>
                                <th style={{ ...thSt, minWidth: 160 }}>{t.colTitle}</th>
                                <th style={{ ...thSt, width: '9.5rem' }}>{t.colType}</th>
                                <th style={{ ...thSt, width: '12rem' }}>{t.colResponsible}</th>
                                <th style={{ ...thSt, width: '8.5rem' }}>{t.colDeadline}</th>
                                <th style={{ ...thSt, width: '9rem' }}>{t.colStatus}</th>
                                <th style={{ ...thSt, width: '8rem' }}>{t.colEvidenceId}</th>
                                <th style={{ ...thSt, width: '8rem' }}>{t.colActions}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {role.trainingActions.map((action) => {
                                const sc = TRAINING_STATUS_COLORS[action.status] || TRAINING_STATUS_COLORS['Planned'];
                                return (
                                  <tr key={action.id}>
                                    <td style={tdSt}>{action.title || '—'}</td>
                                    <td style={tdSt}>{t.typeLabels[action.type] || action.type}</td>
                                    <td style={tdSt}>{resolvePerson(action.responsible, people) || '—'}</td>
                                    <td style={tdSt}>{action.deadline || '—'}</td>
                                    <td style={tdSt}>
                                      <span style={{
                                        ...FONT_SMALL,
                                        background: sc.bg, color: sc.text,
                                        borderRadius: 4, padding: '0.15rem 0.45rem',
                                        fontWeight: 600,
                                      }}>
                                        {t.statusLabels[action.status] || action.status}
                                      </span>
                                    </td>
                                    <td style={tdSt}>{action.evidenceId || '—'}</td>
                                    <td style={{ ...tdSt, whiteSpace: 'nowrap' }}>
                                      <button
                                        onClick={() => handleOpenEditActionForm(role.id, action)}
                                        style={{
                                          ...BTN_SECONDARY, ...FONT_SMALL, borderRadius: 4,
                                          padding: '0.2rem 0.5rem', cursor: 'pointer', marginRight: '0.35rem',
                                        }}
                                      >
                                        {t.editActionBtn}
                                      </button>
                                      <button
                                        onClick={() => handleDeleteAction(role.id, action.id)}
                                        style={{
                                          ...BTN_DESTRUCTIVE_OUTLINE, ...FONT_SMALL, borderRadius: 4,
                                          padding: '0.2rem 0.5rem', cursor: 'pointer',
                                        }}
                                      >
                                        {t.deleteBtn}
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {/* Action form panel */}
                      {editingAction !== null && editingAction.roleId === role.id && (
                        <div style={{
                          background: BG_SUBTLE, border: `1px solid ${BORDER_DEFAULT}`,
                          borderRadius: 6, padding: '1rem', marginBottom: '1rem',
                        }}>
                          <div style={{
                            ...FONT_LABEL, color: TEXT_HEADING, fontWeight: 600, marginBottom: '0.75rem',
                          }}>
                            {editingAction.actionId === 'new' ? t.newActionTitle : t.editActionTitle}
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                            <div style={{ gridColumn: '1 / -1' }}>
                              <label style={labelSt}>{t.colTitle} *</label>
                              <input
                                value={actionForm.title}
                                onChange={e => setActionForm(prev => ({ ...prev, title: e.target.value }))}
                                placeholder={t.titlePlaceholder}
                                style={cellInputSt}
                              />
                            </div>
                            <div>
                              <label style={labelSt}>{t.colType}</label>
                              <select
                                value={actionForm.type}
                                onChange={e => setActionForm(prev => ({ ...prev, type: e.target.value }))}
                                style={{ ...cellInputSt, cursor: 'pointer', width: '100%' }}
                              >
                                {TRAINING_TYPES.map(tp => (
                                  <option key={tp} value={tp}>{t.typeLabels[tp]}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label style={labelSt}>{t.colResponsible}</label>
                              <PeopleSelector
                                orgId={org.id}
                                lang={lang}
                                value={actionForm.responsible}
                                onChange={val => setActionForm(prev => ({ ...prev, responsible: val }))}
                              />
                            </div>
                            <div>
                              <label style={labelSt}>{t.colDeadline}</label>
                              <input
                                type="date"
                                value={actionForm.deadline}
                                onChange={e => setActionForm(prev => ({ ...prev, deadline: e.target.value }))}
                                style={cellInputSt}
                              />
                            </div>
                            <div>
                              <label style={labelSt}>{t.colStatus}</label>
                              <select
                                value={actionForm.status}
                                onChange={e => setActionForm(prev => ({ ...prev, status: e.target.value }))}
                                style={{ ...cellInputSt, cursor: 'pointer', width: '100%' }}
                              >
                                {TRAINING_STATUSES.map(s => (
                                  <option key={s} value={s}>{t.statusLabels[s]}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label style={labelSt}>{t.colEvidenceId}</label>
                              <input
                                value={actionForm.evidenceId}
                                onChange={e => setActionForm(prev => ({ ...prev, evidenceId: e.target.value }))}
                                placeholder={t.evidencePlaceholder}
                                style={cellInputSt}
                              />
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
                            <button
                              onClick={handleSaveActionForm}
                              disabled={!actionForm.title.trim()}
                              style={{
                                ...BTN_PRIMARY, ...FONT_BUTTON, border: 'none', borderRadius: 6,
                                padding: '0.4rem 0.9rem',
                                cursor: actionForm.title.trim() ? 'pointer' : 'not-allowed',
                                opacity: actionForm.title.trim() ? 1 : 0.5,
                              }}
                            >
                              {t.saveActionBtn}
                            </button>
                            <button
                              onClick={handleCancelActionForm}
                              style={{
                                ...BTN_SECONDARY, ...FONT_BUTTON, borderRadius: 6,
                                padding: '0.4rem 0.9rem', cursor: 'pointer',
                              }}
                            >
                              {t.cancelActionBtn}
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Bottom toolbar: add + AI suggest */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => handleOpenNewActionForm(role.id)}
                          style={{
                            ...BTN_SECONDARY, ...FONT_LABEL, borderRadius: 6, padding: '0.35rem 0.85rem',
                            cursor: 'pointer',
                          }}
                        >
                          {t.addAction}
                        </button>
                        <AISuggestButton
                          onClick={() => handleSuggestActions(role.id)}
                          loading={aiActions.loading}
                          disabled={noConsent || aiActions.loading}
                          lang={lang}
                          label={AI_LABEL_ACTIONS}
                        />
                        {noConsent && (
                          <span style={{
                            ...FONT_TINY, color: TEXT_DISABLED,
                          }}>
                            {t.aiNoConsent}
                          </span>
                        )}
                        {aiActions.error && (
                          <span style={{
                            ...FONT_SMALL, color: ERROR_TEXT,
                          }}>
                            {aiActions.error}
                          </span>
                        )}
                      </div>
                    </div>

                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Add role button ─────────────────────────────────────────────────── */}
      <div style={{ marginTop: '1rem' }}>
        <button
          onClick={handleAddRole}
          style={{
            ...BTN_PRIMARY, ...FONT_BUTTON, border: 'none', borderRadius: 8,
            padding: '0.55rem 1.1rem', cursor: 'pointer',
          }}
        >
          {t.addRole}
        </button>
      </div>

      {/* Sticky action bar */}
      <div style={{
        position: 'sticky', bottom: 0,
        marginLeft: '-1.5rem', marginRight: '-1.5rem', marginBottom: '-2.5rem',
        marginTop: '1.5rem',
        padding: '0.85rem 1.5rem', background: BG_SURFACE,
        borderTop: `1px solid ${BORDER_DEFAULT}`, zIndex: 10,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.75rem', flexWrap: 'wrap' }}>
          {dirty && (
            <span style={{ ...FONT_LABEL, color: UNSAVED_AMBER }}>
              {t.unsavedLabel}
            </span>
          )}
          <button
            onClick={handleSave}
            disabled={isSaving}
            style={{
              ...BTN_PRIMARY, ...FONT_BUTTON, background: saveBg, border: 'none',
              borderRadius: 8, padding: '0.55rem 1.1rem',
              cursor: isSaving ? 'not-allowed' : 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {saveLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
